import type { ResolvedQuestionnaire, ResolvedQuestion, ResolvedQuestionGroup } from "./api";
import type { Page, Part, ResolvedResponseType } from "./types/resolved";
import type { Visibility, Requirement } from "./types/common";

const INDENT = "  ";

const visibilityText = (v: Visibility): string => (v === "visible" ? "visible" : `visibleWhen: ${v.visibleWhen}`);

const requirementText = (r: Requirement | undefined): string => {
  if (!r || r === "optional") return "optional";
  if (r === "required") return "required";
  return `requiredWhen: ${r.requiredWhen}`;
};

const keySuffix = (semanticKey: string | null | undefined): string => (semanticKey ? ` {key: ${semanticKey}}` : "");

const responseTypeLines = (
  rt: ResolvedResponseType | null,
  depth: number,
  optionSemanticKeys: Record<string, string>
): string[] => {
  const pad = INDENT.repeat(depth);
  if (!rt) return [`${pad}response: (none)`];
  switch (rt._type) {
    case "select":
    case "select-or-other":
    case "multi-select":
    case "multi-select-or-other": {
      const lines = [`${pad}response: ${rt._type}`];
      for (const v of rt.variants) {
        lines.push(
          `${pad}${INDENT}- "${v.text}" [${v.feel_id}]${
            v.parent_label ? ` (parent: ${v.parent_label})` : ""
          }${keySuffix(optionSemanticKeys[v.feel_id])}`
        );
      }
      return lines;
    }
    case "multi-select-grouped": {
      const lines = [`${pad}response: multi-select-grouped`];
      for (const g of rt.groups) {
        lines.push(`${pad}${INDENT}group "${g.label}":`);
        for (const v of g.variants) {
          lines.push(`${pad}${INDENT}${INDENT}- "${v.text}" [${v.feel_id}]${keySuffix(optionSemanticKeys[v.feel_id])}`);
        }
      }
      return lines;
    }
    case "checkbox":
      return [`${pad}response: checkbox`];
    case "number-range": {
      const fmt = (e: { value: number; label?: string; color?: string }) =>
        `${e.value}${e.label ? ` "${e.label}"` : ""}${e.color ? ` (${e.color})` : ""}`;
      return [`${pad}response: number-range min=${fmt(rt.min)} max=${fmt(rt.max)}`];
    }
    case "integer":
      return [`${pad}response: integer ${rt.min}..${rt.max}`];
    case "vas":
      return [`${pad}response: vas ${rt.min}..${rt.max}`];
    case "date":
      return [`${pad}response: date`];
    case "text": {
      const parts = ["text"];
      if (rt.multiline) parts.push("multiline");
      if (rt.min_length !== undefined) parts.push(`min=${rt.min_length}`);
      if (rt.max_length !== undefined) parts.push(`max=${rt.max_length}`);
      return [`${pad}response: ${parts.join(" ")}`];
    }
    case "text-area":
      return [`${pad}response: text-area`];
  }
};

const questionLines = (
  q: ResolvedQuestion,
  visibility: Visibility,
  requirement: Requirement | undefined,
  depth: number
): string[] => {
  const pad = INDENT.repeat(depth);
  const lines = [
    `${pad}Question [${q.feel_id}]: ${visibilityText(visibility)}; ${requirementText(requirement)}`,
    `${pad}${INDENT}prompt: ${q.question}`,
  ];
  if (q.description) lines.push(`${pad}${INDENT}description: ${q.description}`);
  if (q.semantic_key) lines.push(`${pad}${INDENT}semantic_key: ${q.semantic_key}`);
  lines.push(...responseTypeLines(q.response_type, depth + 1, q.option_semantic_keys ?? {}));
  return lines;
};

const groupLines = (g: ResolvedQuestionGroup, visibility: Visibility, depth: number): string[] => {
  const pad = INDENT.repeat(depth);
  const lines = [`${pad}Group "${g.name}": ${visibilityText(visibility)}`];
  if (g.title) lines.push(`${pad}${INDENT}title: ${g.title}`);
  if (g.description) lines.push(`${pad}${INDENT}description: ${g.description}`);
  for (const qRef of g.questions) {
    lines.push(...questionLines(qRef.question, qRef.visibility, qRef.requirement, depth + 1));
  }
  return lines;
};

const partLines = (part: Part, depth: number): string[] => {
  if (part._type === "question") {
    return questionLines(part.question, part.visibility, part.requirement, depth);
  }
  return groupLines(part.question_group, part.visibility, depth);
};

const pageLines = (page: Page, idx: number): string[] => {
  const lines = [`Page ${idx + 1}: ${page.title ?? ""}`];
  for (const part of page.parts) {
    lines.push(...partLines(part, 1));
  }
  return lines;
};

export const serializeResolvedQuestionnaire = (q: ResolvedQuestionnaire): string => {
  const lines: string[] = [`Questionnaire: ${q.name}`, ""];
  q.pages.forEach((page, idx) => {
    lines.push(...pageLines(page, idx));
    lines.push("");
  });
  return lines.join("\n");
};
